import { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { SelectOption } from './Select';

export interface MultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** How many chips render inline on the trigger before collapsing to "+N more". */
  maxInlineChips?: number;
  /** Optional ceiling on how many can be picked (eg. a plan-tier limit). */
  max?: number;
  /** Show the search box at this option count and above (default 6). */
  searchableAt?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Generic searchable multi-select. Trigger renders selected options as
 * removable chips (with their `prefix` glyph); popover lists every
 * option with a checkable row + free-text search across label / value /
 * hint / searchAliases.
 *
 * Country + currency variants are thin wrappers over this primitive
 * (see MultiCountrySelect).
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  maxInlineChips = 3,
  max,
  searchableAt = 6,
  className,
  ariaLabel,
}: MultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const valueSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => {
      const hay = [o.value, o.label, o.hint, ...(o.searchAliases ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQuery('');
  }, [open]);

  function toggle(v: string) {
    if (valueSet.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      if (max !== undefined && value.length >= max) return;
      onChange([...value, v]);
    }
  }

  function removeOne(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  const selected = useMemo(
    () => value.map((v) => options.find((o) => o.value === v)).filter((x): x is SelectOption => Boolean(x)),
    [value, options],
  );

  const inlineChips = selected.slice(0, maxInlineChips);
  const overflow = selected.length - inlineChips.length;
  const atCap = max !== undefined && value.length >= max;
  const showSearch = options.length > searchableAt;

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 min-h-[2.5rem]',
            'hover:border-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus-visible:border-accent-500',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'data-[state=open]:border-accent-500 data-[state=open]:ring-2 data-[state=open]:ring-accent-500/30',
            className,
          )}
        >
          <span className="flex flex-wrap items-center gap-1.5 min-w-0">
            {selected.length === 0 && (
              <span className="text-ink-500">
                {placeholder ?? t('common.select_placeholder')}
              </span>
            )}
            {inlineChips.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 rounded-md bg-accent-50 px-2 py-0.5 text-xs text-accent-700"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  removeOne(opt.value);
                }}
                role="button"
                tabIndex={-1}
              >
                {opt.prefix}
                <span>{opt.label}</span>
                <X className="h-3 w-3 opacity-70 hover:opacity-100" />
              </span>
            ))}
            {overflow > 0 && (
              <span className="text-xs text-ink-500">+{overflow}</span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-ink-500 shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 rounded-lg border border-ink-200 bg-white shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
            'w-[var(--radix-popover-trigger-width)] min-w-[14rem]',
          )}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2">
              <Search className="h-3.5 w-3.5 text-ink-400 shrink-0" aria-hidden />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.search')}
                className="w-full bg-transparent text-sm text-ink-900 focus:outline-none placeholder:text-ink-400"
                autoComplete="off"
                spellCheck={false}
              />
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[11px] text-ink-500 hover:text-ink-900 px-1"
                >
                  {t('common.clear')}
                </button>
              )}
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-500">
                {t('common.no_results')}
              </li>
            )}
            {filtered.map((opt) => {
              const isSelected = valueSet.has(opt.value);
              const lockedOut = !isSelected && atCap;
              return (
                <li key={opt.value} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    disabled={lockedOut || opt.disabled}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                      'hover:bg-ink-100 focus:outline-none focus:bg-ink-100',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      isSelected && 'bg-accent-50 text-accent-700',
                    )}
                  >
                    {opt.prefix && (
                      <span className="shrink-0 inline-flex w-5 items-center justify-center text-base leading-none">
                        {opt.prefix}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="block truncate text-[11px] text-ink-500 num">
                          {opt.hint}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check className="h-3.5 w-3.5 text-accent-600 shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {max !== undefined && (
            <div className="border-t border-ink-200 px-3 py-1.5 text-[11px] text-ink-500">
              {value.length} / {max}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
